"use client";

import React, { useRef, useEffect } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { aiIcons, devopsIcons, fullstackIcons } from "@/lib/iconSets";


interface IconSliderProps {
  className?: string;
  size?: "small" | "medium" | "large";
}

const IconSlider: React.FC<IconSliderProps> = ({ 
  className,
  size = "large" 
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  
  // Get the appropriate icon set based on size
  const getIconSet = () => {
    switch(size) {
      case "small":
        return fullstackIcons;
      case "medium":
        return devopsIcons;
      case "large":
      default:
        return aiIcons;
    }
  };

  const icons = getIconSet();
  
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;
    
    let scrollTop = 0;
    let animationId: number;
    
    // Auto-scroll animation
    const autoScroll = () => {
      if (!slider) return;
      
      const scrollHeight = slider.scrollHeight;
      
      // Adjust scroll speed based on size
      const scrollSpeed = size === "small" ? 0.4 : size === "medium" ? 0.3 : 0.2;
      
      // Increment scroll position
      scrollTop = size === "medium" ? scrollTop - scrollSpeed : scrollTop + scrollSpeed;
      
      // Create perfect loop by resetting when needed
      if (size === "medium") {
        // For medium (scrolling upward), reset when it scrolls too far up
        if (scrollTop <= 0) {
          scrollTop = scrollHeight / 2;
        }
      } else {
        // For other sizes (scrolling downward), reset when it scrolls too far down
        if (scrollTop >= scrollHeight / 2) {
          scrollTop = 0;
        }
      }
      
      slider.scrollTop = scrollTop;
      animationId = requestAnimationFrame(autoScroll);
    };
    
    // Start auto-scrolling with a small delay
    setTimeout(() => {
      scrollTop = slider.scrollTop;
      animationId = requestAnimationFrame(autoScroll);
    }, 1000);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [size]);
  
  // Size-based styling
  const getIconContainerClass = () => {
    switch(size) {
      case "small":
        return "gap-2 px-1";
      case "medium":
        return "gap-3 px-1.5";
      case "large":
      default:
        return "gap-4 px-2";
    }
  };
  
  return (
    <div 
      className={cn(
        "h-full overflow-hidden transition-all duration-300 relative will-change-transform backface-hidden",
        className
      )}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background pointer-events-none z-10" />
      <div 
        ref={sliderRef}
        className={
          "h-full overflow-y-auto scrollbar-hide pointer-events-none select-none will-change-transform backface-hidden"}
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          touchAction: 'none',
          WebkitOverflowScrolling: 'touch',
          userSelect: 'none',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden'
        }}
        onWheel={(e) => e.preventDefault()}
      >
        {/* First set of icons */}
        <div className={cn("flex flex-col", getIconContainerClass())}>
          {icons.map((item, index) => (
            <div 
              key={`first-${index}`} 
              className={cn(
                "flex items-center justify-center aspect-square transform-gpu will-change-transform backface-hidden",
              )}
            >
              <Image 
                src={item.icon} 
                alt={item.name}
                width={100}
                height={100}
                className="w-full h-full object-contain will-change-transform backface-hidden"
                title={item.name}
                loading="lazy"
                quality={75}
                sizes="(max-width: 768px) 50px, 100px"
              />
            </div>
          ))}
        </div>
        
        {/* Second set of icons to create seamless loop */}
        <div className={cn("flex flex-col", getIconContainerClass())}>
          {icons.map((item, index) => (
            <div 
              key={`second-${index}`} 
              className={cn(
                "flex items-center justify-center aspect-square transform-gpu will-change-transform backface-hidden",
              )}
            >
              <Image 
                src={item.icon} 
                alt={item.name}
                width={100}
                height={100}
                className="w-full h-full object-contain will-change-transform backface-hidden"
                title={item.name}
                loading="lazy"
                quality={75}
                sizes="(max-width: 768px) 50px, 100px"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IconSlider; 